import { Badge, Button, Stack, Table, Text, Tooltip } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { PageHeader, useSearchParamState } from "@how/ui";
import type { AudienceChannel } from "./audience-queries";
import { CampaignModal } from "./CampaignModal";
import { CommunicationsTabs } from "./CommunicationsTabs";
import { type CampaignRow, campaignStatusBadge, useCampaigns } from "./communications-queries";

const CHANNEL_LABEL: Record<AudienceChannel, string> = { sms: "Text", email: "Email" };

const when = (iso: string | null) =>
  iso === null
    ? ""
    : new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * Campaigns (communications plan task 7): compose, see the dry run, submit for approval, approve, schedule, and read
 * what happened. The list polls while anything is scheduled or sending.
 */
export function CampaignsPage() {
  const canCompose = usePermission("communications.compose", { write: true });
  const campaigns = useCampaigns();
  const [open, setOpen] = useState<CampaignRow | null | "new">(null);
  // A campaign named in the address opens on arrival: Reports → Messaging links each campaign here (ADR 0052).
  const [linkedId, setLinkedId] = useSearchParamState("campaign", "");
  const linked =
    linkedId === "" ? null : ((campaigns.data ?? []).find((c) => c.id === linkedId) ?? null);
  const shown = open ?? linked;

  return (
    <>
      <PageHeader
        title="Communications"
        description="Who the church can reach, what it says, and what it has sent."
        actions={
          canCompose.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setOpen("new");
              }}
            >
              New campaign
            </Button>
          ) : null
        }
      />
      <CommunicationsTabs active="campaigns" />
      <Stack gap="md">
        {(campaigns.data ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            No campaigns yet.
          </Text>
        ) : (
          <div className="how-table-frame" style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing={9}
              className="how-table"
              highlightOnHover
              aria-label="Campaigns"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Audience</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th className="how-numeric">Scheduled</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(campaigns.data ?? []).map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{ cursor: "pointer" }}
                    tabIndex={0}
                    onClick={() => {
                      setOpen(row);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") setOpen(row);
                    }}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {row.templateName ?? "No template yet"}
                      </Text>
                    </Table.Td>
                    <Table.Td>{CHANNEL_LABEL[row.channel]}</Table.Td>
                    <Table.Td>{row.audienceName ?? "No audience yet"}</Table.Td>
                    <Table.Td>
                      <StatusCell row={row} />
                    </Table.Td>
                    <Table.Td className="how-numeric">{when(row.scheduledFor)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>
      {shown === null ? null : (
        <CampaignModal
          key={shown === "new" ? "new" : shown.id}
          campaign={shown === "new" ? null : shown}
          onClose={() => {
            setOpen(null);
            setLinkedId(undefined);
          }}
        />
      )}
    </>
  );
}

/**
 * The campaign's status; "Sent · 28 skipped (no consent)" in the warning colour when anyone was left out (#340), with
 * who and why on hover. The campaign itself, one click away, gives every reason.
 */
function StatusCell({ row }: { row: CampaignRow }) {
  const badge = campaignStatusBadge(row);
  const element = (
    <Badge
      variant="light"
      color={badge.color}
      data-campaign-status={badge.notAllSent ? "not_all_sent" : row.status}
    >
      {badge.label}
    </Badge>
  );
  return badge.explanation === null ? (
    element
  ) : (
    <Tooltip label={badge.explanation} multiline w={320} withArrow>
      {element}
    </Tooltip>
  );
}
